import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import { CrewingHorizontalScrollView, CrewingVerticalScrollView } from './webScrollStyles';

export interface EfficiencyVarianceGridRow {
  label: string;
  values: number[];
}

interface EfficiencyVarianceHeatGridCardProps {
  title: string;
  description?: string;
  columnLabels: string[];
  rows: EfficiencyVarianceGridRow[];
}

const getCellColor = (value: number): string => {
  if (value > 15) return '#C84A2B';
  if (value > 8) return '#F0A637';
  if (value >= -5) return '#7FA9D9';
  return '#2E73BD';
};

const formatVariance = (value: number) => `${value > 0 ? '+' : ''}${value}%`;

const EfficiencyVarianceHeatGridCard: React.FC<EfficiencyVarianceHeatGridCardProps> = ({
  title,
  description,
  columnLabels,
  rows,
}) => {
  const { width: screenWidth } = useWindowDimensions();
  const isWeb = Platform.OS === 'web';
  const [cardWidth, setCardWidth] = useState(0);

  // Calculate dynamic column width to fill available space (use measured card width on web)
  const columnWidth = useMemo(() => {
    if (isWeb && cardWidth > 0) {
      const cardPadding = 14 * 2; // matches styles.card.padding
      const rowHeaderWidth = 82;
      const gap = 2; // column gap
      const totalGaps = Math.max(0, columnLabels.length - 1) * gap;
      const innerWidth = Math.max(0, cardWidth - cardPadding);
      const availableWidth = innerWidth - rowHeaderWidth - totalGaps - 8; // small margin buffer
      const colWidth = Math.max(Math.floor(availableWidth / columnLabels.length), 56);
      return colWidth;
    }
    if (!isWeb && screenWidth > 0) return 56; // iOS fixed width
    return 56;
  }, [isWeb, cardWidth, screenWidth, columnLabels.length]);

  // compute leftover pixels to add to the last column so total fills card
  const extraForLast = useMemo(() => {
    if (!isWeb || cardWidth <= 0) return 0;
    const cardPadding = 14 * 2;
    const rowHeaderWidth = 82;
    const gap = 2;
    const totalGaps = Math.max(0, columnLabels.length - 1) * gap;
    const innerWidth = Math.max(0, cardWidth - cardPadding);
    const totalCols = columnWidth * columnLabels.length;
    const extra = innerWidth - rowHeaderWidth - totalGaps - totalCols - 8; // small buffer used earlier
    return extra > 0 ? extra : 0;
  }, [isWeb, cardWidth, columnWidth, columnLabels.length]);

  return (
    <View style={styles.card}>
      <View style={[styles.titleWrap, Platform.OS === 'web' && styles.titleWrapWeb]}>
        <Text style={styles.title}>{title}</Text>
        {description ? <Text style={[styles.description, Platform.OS === 'web' && styles.descriptionWeb, Platform.OS === 'ios' && styles.descriptionIos]}>{description}</Text> : null}
      </View>

      <View style={styles.legendRow}>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: '#C84A2B' }]} />
          <Text style={styles.legendText}>15% + over</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: '#F0A637' }]} />
          <Text style={styles.legendText}>8 to 15% over</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: '#7FA9D9' }]} />
          <Text style={styles.legendText}>near model</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendSwatch, { backgroundColor: '#2E73BD' }]} />
          <Text style={styles.legendText}>5% + under</Text>
        </View>
      </View>

      <View style={styles.table} onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}>
        <CrewingHorizontalScrollView horizontal nestedScrollEnabled showsHorizontalScrollIndicator contentContainerStyle={{ flexGrow: 1 }}>
          <View style={{ minWidth: isWeb && cardWidth ? cardWidth - 28 : 'auto' }}>
            <View style={styles.headerRow}>
              <Text style={[styles.headerCell, styles.rowHeaderCell]}>Zone</Text>
              {columnLabels.map((label, idx) => {
                const isLast = idx === columnLabels.length - 1;
                const width = isLast && extraForLast ? columnWidth + extraForLast : columnWidth;
                return (
                  <View key={label} style={[styles.columnHeader, { width, minWidth: width }]}>
                    <Text style={styles.headerCell}>{label}</Text>
                  </View>
                );
              })}
            </View>

            <View style={styles.bodyViewport}>
              <CrewingVerticalScrollView nestedScrollEnabled showsVerticalScrollIndicator>
                {rows.map((row) => (
                  <View key={row.label} style={styles.dataRow}>
                    <Text style={[styles.rowLabel, styles.rowHeaderCell]}>{row.label}</Text>
                    {row.values.map((value, index) => {
                      const isLast = index === row.values.length - 1;
                      const width = isLast && extraForLast ? columnWidth + extraForLast : columnWidth;
                      return (
                        <View key={`${row.label}-${index}`} style={[styles.valueCell, { backgroundColor: getCellColor(value), width, minWidth: width }]}>
                          <Text style={styles.valueText}>{formatVariance(value)}</Text>
                        </View>
                      );
                    })}
                  </View>
                ))}
              </CrewingVerticalScrollView>
            </View>
          </View>
        </CrewingHorizontalScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    padding: 14,
    flex: 1,
    minHeight: 288,
    flexDirection: 'column',
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#1A1D21',
  },
  titleWrap: {
    flexDirection: 'column',
  },
  titleWrapWeb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    columnGap: 12,
  },
  description: {
    marginTop: 2,
    marginBottom: 12,
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#838383',
  },
  descriptionWeb: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400',
    marginTop: 0,
    marginLeft: 12,
    flexShrink: 1,
    textAlign: 'right',
  },
  descriptionIos: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400',
  },
  table: {
    gap: 0,
    flex: 1,
    flexDirection: 'column',
  },
  bodyViewport: {
    flex: 1,
    minHeight: 160,
    maxHeight: 240,
  },
  headerRow: {
    flexDirection: 'row',
    columnGap: 2,
    marginBottom: 2,
  },
  columnHeader: {
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dataRow: {
    flexDirection: 'row',
    columnGap: 2,
  },
  rowHeaderCell: {
    width: 82,
    minWidth: 82,
  },
  headerCell: {
    textTransform: 'uppercase',
    fontSize: 10,
    color: '#6B7280',
    fontWeight: '700',
    textAlign: 'center',
  },
  rowLabel: {
    fontSize: 12,
    color: '#1F2937',
    fontWeight: '600',
    paddingVertical: 8,
  },
  valueCell: {
    borderRadius: 4,
    height: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  valueText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },
  legendRow: {
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#E3E9F3',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendSwatch: {
    width: 14,
    height: 8,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 11,
    color: '#6B7280',
  },
});

export default EfficiencyVarianceHeatGridCard;
